import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  liveCustomDomain,
  type SeoCompany,
  type SeoCustomPage,
  type SeoHours,
  type SeoPageRow,
  type SeoService,
} from '@/lib/localSeo';

// Some of these columns (custom_domains.tls_status, page_seo_settings) are not
// in the generated Supabase types yet.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export interface LocalSeoData {
  company: SeoCompany | null;
  services: SeoService[];
  hours: SeoHours[];
  /** The tenant's own domain once it's live (verified + TLS active). */
  customDomain: string | null;
}

/**
 * Everything the public site needs for smart default titles/descriptions and
 * the homepage LocalBusiness JSON-LD. All of it is public (anon-readable) data —
 * the same rows api/tenant-page.ts reads for crawlers.
 */
export function useLocalSeoData(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['local-seo', orgId],
    enabled: Boolean(orgId),
    staleTime: 60_000,
    queryFn: async (): Promise<LocalSeoData> => {
      const [companyRes, servicesRes, hoursRes, domainsRes] = await Promise.all([
        db
          .from('company_info')
          .select('company_name, logo_url, phone_number, service_areas, social_links, currency_code, updated_at')
          .eq('organization_id', orgId)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
        db
          .from('services')
          .select('name, description, price, is_quote_only, category, sort_order')
          .eq('organization_id', orgId)
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
        db.from('business_hours').select('day_of_week, is_open, open_time, close_time').eq('organization_id', orgId),
        db.from('custom_domains').select('domain, verified_at, tls_status').eq('organization_id', orgId),
      ]);
      return {
        company: (companyRes?.data as SeoCompany | null) ?? null,
        services: Array.isArray(servicesRes?.data) ? (servicesRes.data as SeoService[]) : [],
        hours: Array.isArray(hoursRes?.data) ? (hoursRes.data as SeoHours[]) : [],
        customDomain: liveCustomDomain(Array.isArray(domainsRes?.data) ? domainsRes.data : []),
      };
    },
  });
}

export interface SeoAdminData {
  company: (SeoCompany & { google_place_id?: string | null; google_reviews_enabled?: boolean | null }) | null;
  services: SeoService[];
  seoRows: SeoPageRow[];
  customPages: SeoCustomPage[];
  domains: Array<{ domain: string; verified_at: string | null; tls_status: string | null }>;
}

/** Owner-side data for the "Google & SEO" check (admin only). */
export function useSeoAdminData(orgId: string | null | undefined) {
  return useQuery({
    queryKey: ['seo-admin-check', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<SeoAdminData> => {
      const [companyRes, servicesRes, seoRes, pagesRes, domainsRes] = await Promise.all([
        // `*` so it degrades gracefully if a column (e.g. google_place_id) isn't migrated yet.
        db.from('company_info').select('*').eq('organization_id', orgId).order('updated_at', { ascending: false }).limit(1).maybeSingle(),
        db
          .from('services')
          .select('name, description, price, is_quote_only, category, sort_order')
          .eq('organization_id', orgId)
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
        db
          .from('page_seo_settings')
          .select('page_slug, seo_title, meta_description, indexing_preference, canonical_url, updated_at')
          .eq('organization_id', orgId),
        db.from('custom_pages').select('slug, title, meta_description, is_published, updated_at').eq('organization_id', orgId).order('sort_order'),
        db.from('custom_domains').select('domain, verified_at, tls_status').eq('organization_id', orgId),
      ]);
      if (companyRes?.error) throw companyRes.error;
      return {
        company: companyRes?.data ?? null,
        services: servicesRes?.data ?? [],
        seoRows: seoRes?.data ?? [],
        customPages: pagesRes?.data ?? [],
        domains: domainsRes?.data ?? [],
      };
    },
  });
}
